import React, { createContext, HTMLAttributes, ReactText, useCallback, useState } from 'react';
import '../../base.scss';
import './RadioGroup.scss';
import { classNames } from '../../utilities';

export interface RadioGroupProps extends HTMLAttributes<HTMLInputElement> {
  children: React.ReactNode;
  /** Disable all choices */
  disabled?: boolean;
  /* Default name for each radio */
  name: string;
  /* onChange handler applied to each child radio component */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /* Callback handler for value changes */
  onSelectionChange?: (name: string, value: ReactText) => void;
  /* initialValue - will show that button as checked. */
  selectedValue?: React.ReactText;
}

// grab just the props applicable to children radios
export type RadioGroupContextProps = Pick<RadioGroupProps, 'onChange' | 'name' | 'disabled' | 'selectedValue'>

// create the context with default values
const RadioGroupContext = createContext<RadioGroupContextProps>({
  name: 'RadioGroupContext',
});

export { RadioGroupContext };

/**
 * RadioGroup manages a set of Radio components as children
 */
export function RadioGroup(props: RadioGroupProps) {
  const { children, className, name, disabled, onChange, onSelectionChange, selectedValue } = props;
  const isControlled = (!!onChange || !!onSelectionChange);
  
  const [uncontrolledValue, setUncontrolledValue] = useState(selectedValue);

  const onChangeWrapper = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newValue = event.target.value;

      if (onChange) {
        onChange(event);
      } 

      if (onSelectionChange) {
        onSelectionChange(name, newValue);
      }

      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
    }
  }, [name, onChange, onSelectionChange, isControlled]);

  // hook for passing context values to radios
  const group = React.useMemo(
    () => ({
      disabled,
      name,
      onChange: onChangeWrapper,
      selectedValue: isControlled ? selectedValue : uncontrolledValue
    }),
    [disabled, name, onChangeWrapper, isControlled, selectedValue, uncontrolledValue]
  );

  const radioGroupClassNames = classNames('ps-radio-group', className);

  return (
    <RadioGroupContext.Provider value={group}>
      <div className={radioGroupClassNames}>{children}</div>
    </RadioGroupContext.Provider>
  );
}
